import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    // If returning from Google OAuth, let AuthCallback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("pd_token");
    if (!token) {
      setUser(null);
      setHasProfile(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setHasProfile(data.has_profile);
      try { localStorage.setItem("pd_user", JSON.stringify(data)); } catch {}
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Real auth failure — clear the session.
        localStorage.removeItem("pd_token");
        localStorage.removeItem("pd_user");
        setUser(null);
        setHasProfile(false);
      } else {
        // Network/offline — keep the session and restore the last known identity
        // so offline screens (Emergency, Medical ID) still open.
        try {
          const cached = JSON.parse(localStorage.getItem("pd_user") || "null");
          if (cached?.user) {
            setUser(cached.user);
            setHasProfile(cached.has_profile);
          } else {
            setUser(null);
            setHasProfile(false);
          }
        } catch {
          setUser(null);
          setHasProfile(false);
        }
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("pd_token", data.token);
    await load();
    return data;
  };

  const register = async (email, password, consent) => {
    const { data } = await api.post("/auth/register", {
      email,
      password,
      consent_health_data: consent,
    });
    localStorage.setItem("pd_token", data.token);
    setUser(data.user);
    setHasProfile(false);
    setLoading(false);
    return data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const consent = async () => {
    const { data } = await api.post("/auth/consent");
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("pd_token");
    localStorage.removeItem("pd_user");
    setUser(null);
    setHasProfile(false);
  };

  return (
    <AuthContext.Provider
      value={{ user, hasProfile, loading, login, register, loginWithGoogle, consent, logout, refresh: load, setHasProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
